import { z } from 'zod'
import { objectToCamel, objectToSnake } from 'ts-case-convert'

const AddressTypesEnum = [
  'cmt',
  'customer',
  'dealer',
  'direct_ship',
  'distribution_center',
  'invoice',
  'po',
  'supplier',
] as const

//* ---------------- BASE ---------------- *//

export const baseAddressSchema = z.object({
  id: z.number(),
  contact_name: z.string().nullable(),
  description: z.string(),
  street1: z.string(),
  street2: z.string().nullable(),
  street3: z.string().nullable(),
  city: z.string().nullable(),
  state: z.string().nullable(),
  zip: z.string().nullable(),
  country: z.string(),
  phone: z.string().nullable(),
})

//* ---------------- BASE DATA ---------------- */
const baseModifyAddressData = z.object({
  description: z.string(),
  addressType: z.enum(AddressTypesEnum),
  propertyType: z.enum(['residential', 'commercial']).default('residential'),
  street1: z.string(),
  street2: z.string().nullable().optional(),
  street3: z.string().nullable().optional(),
  city: z.string(),
  state: z.string().nullable().optional(),
  zip: z.string().nullable().optional(),
  country: z.string(),
  phone: z.string().nullable().optional(),
})

//* ---------------- SCHEMAS ---------------- *//
export const addressSchema = baseAddressSchema.transform(objectToCamel)

export const dealerAddressSchema = z
  .object({
    dealer_id: z.number().optional(),
    address_id: z.number().optional(),
    is_billing: z.boolean(),
    is_primary: z.boolean(),
    rack_code: z.string().nullable(),
    active: z.coerce.boolean(),
    address: addressSchema,
  })
  .transform(objectToCamel)

//* ---------------- DATA SCHEMAS ---------------- *//

export const createAddressData = baseModifyAddressData
  .transform(objectToSnake)
  .transform(({ street_1, street_2, street_3, ...fields }) => ({
    street1: street_1,
    street2: street_2,
    street3: street_3,
    ...fields,
  }))

export const updateAddressData = baseModifyAddressData
  .extend({
    description: z.string().optional(),
    addressType: z.enum(AddressTypesEnum).optional(),
    propertyType: z.enum(['residential', 'commercial']).optional(),
    street1: z.string().optional(),
    city: z.string().optional(),
    country: z.string().optional(),
  })
  .transform(objectToSnake)
  .transform(({ street_1, street_2, street_3, ...fields }) => ({
    street1: street_1,
    street2: street_2,
    street3: street_3,
    ...fields,
  }))

export const baseDealerAddressData = z.object({
  isPrimary: z.boolean().default(false),
  isBilling: z.boolean().default(false),
  rackCode: z.string().nullable().optional(),
  active: z.boolean().default(true),
})

export const createDealerAddressData = baseDealerAddressData
  .extend({
    addressId: z.number(),
  })
  .transform(objectToSnake)

export const updateDealerAddressData = baseDealerAddressData.transform(objectToSnake)

//* ---------------- TYPES ---------------- *//
declare global {
  namespace TrinityAPI {
    type AddressType = z.infer<typeof addressSchema>
    type DealerAddressType = z.infer<typeof dealerAddressSchema>
  }
}
